// src/utils/splitBlocks.js
//
// Blocks the page split cut in two, and putting them back together.
//
// When a paragraph, list or table runs past the bottom of a page, the reflow in
// EditorPage.jsx cuts it: the first part stays, the rest becomes a new block at
// the top of the next page. Those two blocks are what gets saved. Nothing ever
// joined them again, so every re-layout that moved a page break — a size
// change, an image switched on or off, a reload — cut more paragraphs and left
// each old cut behind as a real paragraph break. The same text went from 24
// paragraphs to 157.
//
// So a cut is marked, and the reflow joins every marked cut before it lays the
// pages out again. Any number of re-layouts then gives the same result.
//
//   <p data-split-id="s3">… the first part</p>     ← end of one page
//   <p data-split-cont="s3">the rest …</p>         ← top of the next
//
// Ids are numbered in page order within one reflow (s1, s2, …), so laying out
// an unchanged document saves the same HTML.
//
// A continuation joins the block before it only when that block carries its
// id. Pressing Enter at the end of the first part makes the browser copy the
// paragraph, id and all — and the rest then belongs with the new paragraph,
// which is exactly the one before it. Anything else between them (a paste, an
// image) breaks the link and the continuation stays a paragraph of its own.

export const SPLIT_ID_ATTR = 'data-split-id';
export const SPLIT_CONT_ATTR = 'data-split-cont';

/** Mark `cont` as the rest of `original`, cut at a page bottom. */
export function markSplit(original, cont, id) {
  original.setAttribute(SPLIT_ID_ATTR, id);
  cont.setAttribute(SPLIT_CONT_ATTR, id);
  cont.removeAttribute(SPLIT_ID_ATTR);
}

const sameAttributes = (a, b) => {
  if (a.attributes.length !== b.attributes.length) return false;
  for (const { name, value } of Array.from(a.attributes)) {
    if (b.getAttribute(name) !== value) return false;
  }
  return true;
};

// The cut went through the middle of the paragraph's inline formatting too: a
// bold run split in two became two identical <strong>s. Join those back where
// the halves meet, all the way down.
const mergeAtSeam = (a, b) => {
  if (!a || !b || a.nodeType !== 1 || b.nodeType !== 1) return;
  if (a.tagName !== b.tagName || !sameAttributes(a, b)) return;
  const innerA = a.lastChild;
  const innerB = b.firstChild;
  while (b.firstChild) a.appendChild(b.firstChild);
  b.remove();
  mergeAtSeam(innerA, innerB);
};

const isTable = (el) => el.classList?.contains('rv-table-wrap');
const isList = (el) => el.tagName === 'OL' || el.tagName === 'UL';

// Nothing but a line break: what the split leaves in a first part it emptied.
const isPlaceholder = (el) =>
  !(el.textContent || '').trim() &&
  Array.from(el.querySelectorAll('*')).every((n) => n.tagName === 'BR');

// Remove the one <br> that ends `el`, however deep in its last inline element.
// Only one: a line break the author typed at the end of a paragraph is stored
// as two (the break and the browser's filler), and the first must survive.
const dropTrailingBreaks = (el) => {
  let last = el.lastChild;
  while (last) {
    if (last.nodeType === 3 && !last.textContent.trim() && last.previousSibling) {
      last = last.previousSibling; // whitespace after the break
    } else if (last.nodeType === 1 && last.nodeName !== 'BR' && last.lastChild) {
      last = last.lastChild;
    } else {
      break;
    }
  }
  if (last?.nodeName === 'BR') last.remove();
};

function joinInto(prev, cont) {
  if (isTable(prev)) {
    const into = prev.querySelector('.rv-table tbody');
    const from = cont.querySelector('.rv-table tbody');
    if (into && from) while (from.firstChild) into.appendChild(from.firstChild);
    return;
  }
  if (isList(prev)) {
    while (cont.firstChild) prev.appendChild(cont.firstChild);
    return;
  }
  if (isPlaceholder(prev)) prev.innerHTML = '';
  // A <br> at the very end of a paragraph is the browser's filler (it puts one
  // in the new paragraph when Enter is pressed). It draws nothing at the end,
  // but in the middle of the joined paragraph it would be a line break.
  dropTrailingBreaks(prev);
  const a = prev.lastChild;
  const b = cont.firstChild;
  while (cont.firstChild) prev.appendChild(cont.firstChild);
  mergeAtSeam(a, b);
  prev.normalize();
}

/**
 * Join every marked cut back into the block it was cut from, and take the
 * marks off. Returns the blocks still to lay out, in order; joined
 * continuations are removed from the document and are not in it.
 *
 * Run after the image pieces are joined (`joinBleedPieces`): the rest of a
 * wrapped image can sit between a paragraph and its continuation.
 *
 * @param {Element[]} blocks page-body children in document order
 * @returns {Element[]}
 */
export function joinSplitBlocks(blocks) {
  const kept = [];
  for (const block of blocks) {
    const id = block.getAttribute?.(SPLIT_CONT_ATTR);
    if (id != null) {
      block.removeAttribute(SPLIT_CONT_ATTR);
      const prev = kept[kept.length - 1];
      const sameKind = prev && prev.tagName === block.tagName && isTable(prev) === isTable(block);
      if (sameKind && prev.getAttribute(SPLIT_ID_ATTR) === id) {
        // A block cut over three pages: the middle part carries the next id.
        const next = block.getAttribute(SPLIT_ID_ATTR);
        joinInto(prev, block);
        block.remove();
        if (next != null) prev.setAttribute(SPLIT_ID_ATTR, next);
        else prev.removeAttribute(SPLIT_ID_ATTR);
        continue;
      }
    }
    kept.push(block);
  }
  kept.forEach((b) => b.removeAttribute?.(SPLIT_ID_ATTR));
  return kept;
}

/**
 * Take the cut marks off pasted content, so a copied half of a paragraph is
 * never joined to whatever happens to sit before it.
 *
 * @param {Element} root the parsed clipboard content
 */
export function stripSplitMarks(root) {
  root.querySelectorAll(`[${SPLIT_ID_ATTR}], [${SPLIT_CONT_ATTR}]`).forEach((el) => {
    el.removeAttribute(SPLIT_ID_ATTR);
    el.removeAttribute(SPLIT_CONT_ATTR);
  });
}
